import { createFileRoute, Link } from "@tanstack/react-router";
import { MarketingShell } from "@/components/marketing-shell";
import { Button } from "@/components/ui/button";
import { redirectIfAuthenticated } from "@/lib/marketing-guards";

export const Route = createFileRoute("/docs")({
  beforeLoad: redirectIfAuthenticated,
  head: () => ({
    meta: [
      { title: "Documentation — Super Affiliate OS" },
      {
        name: "description",
        content:
          "Complete documentation for Super Affiliate OS — setup guides, feature walkthroughs, GoHighLevel integration SOPs, and admin reference.",
      },
      { property: "og:title", content: "Documentation — Super Affiliate OS" },
      {
        property: "og:description",
        content:
          "Setup guides, SOPs, and feature reference for Super Affiliate OS, including the GoHighLevel integration.",
      },
    ],
  }),
  component: DocsPage,
});

type Section = {
  id: string;
  title: string;
  children?: { id: string; title: string }[];
};

const toc: Section[] = [
  {
    id: "getting-started",
    title: "Getting started",
    children: [
      { id: "create-account", title: "Create your account" },
      { id: "first-workspace", title: "Create your first workspace" },
      { id: "invite-team", title: "Invite your team" },
      { id: "tour", title: "Take the in-app tour" },
    ],
  },
  {
    id: "core-concepts",
    title: "Core concepts",
    children: [
      { id: "workspaces", title: "Workspaces" },
      { id: "affiliate-centers", title: "Affiliate centers" },
      { id: "imports", title: "Imports & reconciliation" },
      { id: "anomalies", title: "Anomalies" },
      { id: "contacts", title: "Contacts & attribution" },
    ],
  },
  {
    id: "ghl-setup",
    title: "GoHighLevel setup (SOP)",
    children: [
      { id: "ghl-overview", title: "Overview" },
      { id: "ghl-prereqs", title: "Prerequisites" },
      { id: "ghl-install", title: "Step 1 — Install the marketplace app" },
      { id: "ghl-connect", title: "Step 2 — Connect your sub-account" },
      { id: "ghl-map", title: "Step 3 — Map fields & tags" },
      { id: "ghl-test", title: "Step 4 — Send a test commission" },
      { id: "ghl-automations", title: "Step 5 — Wire automations" },
      { id: "ghl-troubleshoot", title: "Troubleshooting" },
    ],
  },
  {
    id: "pages",
    title: "Page-by-page reference",
    children: [
      { id: "page-command-center", title: "Command Center" },
      { id: "page-dashboard", title: "Dashboard" },
      { id: "page-workspaces", title: "Workspaces" },
      { id: "page-anomalies", title: "Anomalies" },
      { id: "page-imports", title: "Imports" },
      { id: "page-contacts", title: "Contacts" },
      { id: "page-links", title: "Promotional links" },
      { id: "page-swipe", title: "Swipe files" },
      { id: "page-bonus-builder", title: "Bonus builder" },
      { id: "page-calendar", title: "Calendar" },
      { id: "page-training", title: "Training" },
      { id: "page-digests", title: "Digests" },
      { id: "page-lenses", title: "Lenses" },
      { id: "page-people", title: "People" },
    ],
  },
  {
    id: "settings",
    title: "Settings reference",
    children: [
      { id: "set-profile", title: "Profile" },
      { id: "set-notifications", title: "Notifications" },
      { id: "set-account", title: "Account" },
      { id: "set-workspaces", title: "Workspaces" },
      { id: "set-team", title: "Team & roles" },
      { id: "set-billing", title: "Billing" },
      { id: "set-integrations", title: "Integrations" },
      { id: "set-exports", title: "Exports" },
      { id: "set-support", title: "Support" },
    ],
  },
  {
    id: "billing",
    title: "Plans & billing",
  },
  {
    id: "security",
    title: "Security & data",
  },
  {
    id: "support",
    title: "Getting help",
  },
];

function H2({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2
      id={id}
      className="scroll-mt-24 mt-16 border-b border-border pb-2 text-2xl font-semibold tracking-tight"
    >
      {children}
    </h2>
  );
}

function H3({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h3 id={id} className="scroll-mt-24 mt-10 text-lg font-semibold tracking-tight">
      {children}
    </h3>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="mt-3 text-[15px] leading-7 text-muted-foreground">{children}</p>;
}

function UL({ children }: { children: React.ReactNode }) {
  return (
    <ul className="mt-3 list-disc space-y-1 pl-6 text-[15px] leading-7 text-muted-foreground marker:text-muted-foreground/60">
      {children}
    </ul>
  );
}

function OL({ children }: { children: React.ReactNode }) {
  return (
    <ol className="mt-3 list-decimal space-y-2 pl-6 text-[15px] leading-7 text-muted-foreground marker:text-muted-foreground/60">
      {children}
    </ol>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[13px] text-foreground">
      {children}
    </code>
  );
}

function Callout({
  tone = "info",
  title,
  children,
}: {
  tone?: "info" | "warning" | "success";
  title: string;
  children: React.ReactNode;
}) {
  const styles =
    tone === "warning"
      ? "border-amber-500/30 bg-amber-500/5"
      : tone === "success"
      ? "border-emerald-500/30 bg-emerald-500/5"
      : "border-primary/30 bg-primary/5";
  return (
    <div className={`mt-5 rounded-lg border p-4 ${styles}`}>
      <div className="text-sm font-semibold text-foreground">{title}</div>
      <div className="mt-1 text-sm leading-6 text-muted-foreground">{children}</div>
    </div>
  );
}

function DocsPage() {
  return (
    <MarketingShell>
      <section className="mx-auto max-w-7xl px-6 py-16">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
            Documentation
          </div>
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
            Super Affiliate OS docs
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
            Everything you need to get set up, connect GoHighLevel, and run your
            affiliate business from a single workspace.
          </p>
        </div>

        <div className="mt-16 grid gap-10 lg:grid-cols-[260px_minmax(0,1fr)]">
          {/* TOC */}
          <aside className="lg:sticky lg:top-24 lg:h-[calc(100vh-7rem)] lg:overflow-y-auto">
            <nav className="space-y-6 text-sm">
              {toc.map((s) => (
                <div key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="font-semibold text-foreground hover:text-primary"
                  >
                    {s.title}
                  </a>
                  {s.children && (
                    <ul className="mt-2 space-y-1.5 border-l border-border pl-3">
                      {s.children.map((c) => (
                        <li key={c.id}>
                          <a
                            href={`#${c.id}`}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            {c.title}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </nav>
          </aside>

          {/* Body */}
          <article className="min-w-0">
            {/* ============== Getting started ============== */}
            <H2 id="getting-started">Getting started</H2>
            <P>
              Super Affiliate OS is the operating system for affiliate
              marketers. You connect the platforms you promote on (GoHighLevel,
              ClickFunnels, ThriveCart, ShareASale, Impact, PartnerStack, CJ,
              and any CSV-exporting network), and we unify your commissions,
              contacts, and campaigns into one workspace.
            </P>
            <P>
              This guide walks you from zero to running your first reconciled
              workspace with live anomaly detection.
            </P>

            <H3 id="create-account">Create your account</H3>
            <OL>
              <li>
                Go to{" "}
                <Link to="/signup" className="text-primary hover:underline">
                  /signup
                </Link>{" "}
                and create an account with email + password, or sign in with
                Google.
              </li>
              <li>Verify your email — we send a confirmation link to your inbox.</li>
              <li>
                Pick a plan (Starter, Pro, or Super). Every plan starts with a
                14-day free trial; you won't be charged until day 15.
              </li>
            </OL>

            <H3 id="first-workspace">Create your first workspace</H3>
            <P>
              A <strong>workspace</strong> represents one affiliate program,
              partner, or revenue stream you're tracking (e.g. "ClickFunnels
              affiliate", "GoHighLevel affiliate", "Acme partnership"). You can
              have many workspaces inside one account.
            </P>
            <OL>
              <li>From the sidebar, click <strong>Workspaces → New workspace</strong>.</li>
              <li>
                Name it after the program (e.g. <em>"GoHighLevel Affiliate"</em>),
                pick the platform driver, and continue.
              </li>
              <li>
                Choose how data flows in: <strong>CSV import</strong>,{" "}
                <strong>paste from clipboard</strong>, or{" "}
                <strong>live integration</strong> (GoHighLevel, ClickFunnels,
                webhook).
              </li>
              <li>
                Save. You'll land on the workspace dashboard with empty-state
                prompts for your first import.
              </li>
            </OL>

            <H3 id="invite-team">Invite your team</H3>
            <P>
              Pro and Super plans include teammate seats. Go to{" "}
              <strong>Settings → Team</strong>, click <strong>Invite</strong>,
              and enter an email + role.
            </P>
            <UL>
              <li><strong>Owner</strong> — full control, billing, deletion.</li>
              <li><strong>Admin</strong> — manage workspaces, integrations, and team.</li>
              <li><strong>Member</strong> — read/write inside workspaces they belong to.</li>
              <li><strong>Viewer</strong> — read-only.</li>
            </UL>

            <H3 id="tour">Take the in-app tour</H3>
            <P>
              First time logging in, an interactive tour highlights the
              Command Center, Workspaces, Anomalies, and Settings. You can
              re-trigger it from <strong>Settings → Profile → Restart tour</strong>.
            </P>

            {/* ============== Core concepts ============== */}
            <H2 id="core-concepts">Core concepts</H2>

            <H3 id="workspaces">Workspaces</H3>
            <P>
              A workspace is a self-contained environment for one affiliate
              program. It owns its own commissions, contacts, products,
              anomaly detectors, and integration credentials. Switch workspaces
              from the sidebar dropdown.
            </P>

            <H3 id="affiliate-centers">Affiliate centers</H3>
            <P>
              An <strong>Affiliate Center</strong> is the live connection to a
              platform you promote (GoHighLevel, ClickFunnels, etc.). Each
              center polls or receives webhooks, normalizes the data, and
              writes it into the workspace.
            </P>

            <H3 id="imports">Imports & reconciliation</H3>
            <P>
              When you upload a CSV or receive a webhook, the importer:
            </P>
            <OL>
              <li>Auto-detects columns (amount, email, date, status).</li>
              <li>Dedupes against existing rows using a stable hash.</li>
              <li>
                Reconciles status changes (pending → paid → refunded) without
                creating duplicates.
              </li>
              <li>Flags unclassified products so you can map them.</li>
            </OL>

            <H3 id="anomalies">Anomalies</H3>
            <P>
              The anomaly engine watches for revenue drops, refund spikes,
              missing recurring commissions, and conversion-rate cliffs. Each
              detector has thresholds you can tune in{" "}
              <strong>Workspace → Anomalies → Detectors</strong>.
            </P>

            <H3 id="contacts">Contacts & attribution</H3>
            <P>
              Every commission is tied to a contact (by email or external ID).
              Contacts unify across imports so you see lifetime value, repeat
              purchases, and which campaign earned them.
            </P>

            {/* ============== GHL SETUP ============== */}
            <H2 id="ghl-setup">GoHighLevel setup (SOP)</H2>

            <H3 id="ghl-overview">Overview</H3>
            <P>
              The GoHighLevel integration syncs contacts, opportunities, and
              affiliate commission events from any GHL sub-account into a Super
              Affiliate OS workspace. Once connected, you can:
            </P>
            <UL>
              <li>Pull commissions into your dashboard automatically.</li>
              <li>Push attribution data (campaign, click ID) back into GHL contact custom fields.</li>
              <li>Trigger GHL automations when an anomaly fires (e.g. "VIP refunded → send Slack").</li>
              <li>Use GHL tags to segment your affiliate audience inside Super Affiliate OS.</li>
            </UL>

            <H3 id="ghl-prereqs">Prerequisites</H3>
            <UL>
              <li>An active GoHighLevel <strong>Agency</strong> or <strong>Sub-account</strong> with Marketplace access.</li>
              <li>Admin role on the GHL location you want to connect.</li>
              <li>A Super Affiliate OS workspace created (see <a href="#first-workspace" className="text-primary hover:underline">above</a>).</li>
              <li>Owner or Admin role in Super Affiliate OS.</li>
            </UL>

            <H3 id="ghl-install">Step 1 — Install the marketplace app</H3>
            <OL>
              <li>
                In GoHighLevel, open{" "}
                <strong>Settings → Integrations → Marketplace</strong> and
                search for <strong>Super Affiliate OS</strong>.
              </li>
              <li>Click <strong>Install</strong>.</li>
              <li>
                Select the sub-account (location) you want to connect. You can
                install to multiple locations later.
              </li>
              <li>
                Review the requested scopes (contacts read/write, opportunities
                read, custom fields read/write, workflows read) and click{" "}
                <strong>Authorize</strong>.
              </li>
            </OL>
            <Callout tone="info" title="Agency vs sub-account">
              If you're an agency, install at the agency level once, then enable
              per-location from inside Super Affiliate OS. Sub-account-only
              accounts install directly to that one location.
            </Callout>

            <H3 id="ghl-connect">Step 2 — Connect your sub-account</H3>
            <OL>
              <li>
                In Super Affiliate OS, open{" "}
                <strong>Settings → Integrations</strong> and click{" "}
                <strong>Connect GoHighLevel</strong>.
              </li>
              <li>
                You'll be redirected to GHL's OAuth screen. Pick the location
                and approve.
              </li>
              <li>
                You'll land back in Super Affiliate OS with a green{" "}
                <strong>Connected</strong> badge and the location name.
              </li>
              <li>
                Pick the <strong>workspace</strong> this GHL connection should
                feed into.
              </li>
            </OL>

            <H3 id="ghl-map">Step 3 — Map fields & tags</H3>
            <P>
              GoHighLevel stores affiliate data in <em>custom fields</em> on the
              contact. We need to know which fields hold what.
            </P>
            <OL>
              <li>
                Go to <strong>Workspace → Settings → Field mapping</strong>.
              </li>
              <li>
                For each Super Affiliate OS field on the left, pick the GHL
                custom field on the right:
                <UL>
                  <li><Code>commission_amount</Code> → GHL <Code>Commission Amount</Code></li>
                  <li><Code>commission_status</Code> → GHL <Code>Commission Status</Code> (pending/paid/refunded)</li>
                  <li><Code>affiliate_id</Code> → GHL <Code>Affiliate ID</Code></li>
                  <li><Code>campaign_source</Code> → GHL <Code>UTM Source</Code></li>
                  <li><Code>product_name</Code> → GHL <Code>Product</Code></li>
                </UL>
              </li>
              <li>
                Map <strong>tags</strong> if you use them to mark VIPs,
                refunders, or specific funnels.
              </li>
              <li>Click <strong>Save mapping</strong>.</li>
            </OL>
            <Callout tone="warning" title="Custom field doesn't exist yet?">
              Create it in GHL first: <strong>Settings → Custom Fields → Add</strong>.
              Use type "Monetary" for amounts and "Single Line" for IDs/strings.
              Then come back and refresh the mapping screen.
            </Callout>

            <H3 id="ghl-test">Step 4 — Send a test commission</H3>
            <OL>
              <li>
                In GHL, open any contact and set their{" "}
                <Code>Commission Amount</Code> to <Code>1.00</Code> and{" "}
                <Code>Commission Status</Code> to <Code>paid</Code>.
              </li>
              <li>Save the contact.</li>
              <li>
                Within ~30 seconds, you should see the row appear in{" "}
                <strong>Workspace → Contacts</strong> with a $1.00 commission.
              </li>
              <li>
                If it doesn't show up, check{" "}
                <strong>Settings → Integrations → GoHighLevel → Logs</strong>{" "}
                for the most recent sync attempt.
              </li>
            </OL>

            <H3 id="ghl-automations">Step 5 — Wire automations</H3>
            <P>
              Connect anomalies and events back into GHL workflows so action
              happens automatically.
            </P>
            <UL>
              <li>
                <strong>Anomaly → GHL workflow:</strong> In{" "}
                <strong>Workspace → Anomalies → Detector → Actions</strong>,
                pick <em>Trigger GHL workflow</em> and choose a workflow by
                name (e.g. "VIP Refund Recovery").
              </li>
              <li>
                <strong>Commission paid → tag contact:</strong> Add a push rule
                that tags the contact <Code>commission-paid</Code> so your GHL
                workflows can react.
              </li>
              <li>
                <strong>Attribution push:</strong> When Super Affiliate OS
                resolves which campaign earned the click, it writes the value
                back into the <Code>campaign_source</Code> custom field.
              </li>
            </UL>

            <H3 id="ghl-troubleshoot">Troubleshooting</H3>
            <UL>
              <li>
                <strong>"OAuth invalid_grant":</strong> The connection token
                expired. Click <strong>Reconnect</strong> in Settings →
                Integrations.
              </li>
              <li>
                <strong>Commissions not appearing:</strong> 95% of the time
                this is a field-mapping issue. Open the GHL contact, confirm
                the custom field actually has a value, and re-check the
                mapping screen.
              </li>
              <li>
                <strong>Duplicate contacts:</strong> Make sure the GHL contact
                has an email. We dedupe by email; contacts with only a phone
                number are treated as separate.
              </li>
              <li>
                <strong>Webhook delays:</strong> GHL batches webhook delivery.
                Expect up to 60 seconds of lag during high-volume periods.
              </li>
            </UL>

            {/* ============== Pages reference ============== */}
            <H2 id="pages">Page-by-page reference</H2>

            <H3 id="page-command-center">Command Center</H3>
            <P>
              Your global home. Aggregates revenue, top campaigns, recent
              anomalies, and active workspaces across every affiliate center
              you've connected. Pinned widgets are configurable.
            </P>

            <H3 id="page-dashboard">Dashboard</H3>
            <P>
              Per-workspace dashboard: revenue chart, top products, recent
              imports, recent pushes to GHL, and open anomalies for this
              workspace only.
            </P>

            <H3 id="page-workspaces">Workspaces</H3>
            <P>
              Index of all workspaces in your account. Create, archive, or
              switch into any workspace. Each workspace card shows live
              revenue, last sync time, and unresolved anomaly count.
            </P>

            <H3 id="page-anomalies">Anomalies</H3>
            <P>
              The anomaly inbox. Each card explains what fired, what changed,
              and what to do. Resolve, snooze, or escalate to a teammate.
              Detector thresholds and notification channels live in{" "}
              <strong>Anomalies → Detectors</strong>.
            </P>

            <H3 id="page-imports">Imports</H3>
            <P>
              History of every CSV upload, paste, or webhook batch. Click any
              import to see column mapping, row counts, errors, and
              reconciliation results. Failed imports can be re-run after
              fixing the source file.
            </P>

            <H3 id="page-contacts">Contacts</H3>
            <P>
              Unified contact list across all sources for the workspace. Each
              contact shows lifetime commission, source breakdown, and
              attribution path. Use filters to find refunders, VIPs, or
              recently active.
            </P>

            <H3 id="page-links">Promotional links</H3>
            <P>
              Generate short, tracked links for any campaign. Each link
              records clicks, devices, and conversion attribution. Pair a
              link with a GHL workflow ID to auto-tag clickers.
            </P>

            <H3 id="page-swipe">Swipe files</H3>
            <P>
              Your library of email copy, social posts, and ad creatives,
              organized by product and campaign. Share with teammates or
              copy/paste into GHL email templates.
            </P>

            <H3 id="page-bonus-builder">Bonus builder</H3>
            <P>
              Build downloadable bonus packages to offer alongside affiliate
              promotions. Drag in PDFs, videos, and templates, then generate
              a hosted delivery page you can drop into any funnel.
            </P>

            <H3 id="page-calendar">Calendar</H3>
            <P>
              Promotional calendar showing every scheduled campaign across
              workspaces. Plan launches, anniversary promos, and reorder
              cadences. Drag to reschedule.
            </P>

            <H3 id="page-training">Training</H3>
            <P>
              Built-in courses on affiliate marketing fundamentals, advanced
              attribution, and platform-specific playbooks (including a full
              GoHighLevel affiliate course).
            </P>

            <H3 id="page-digests">Digests</H3>
            <P>
              Daily and weekly email summaries of revenue, anomalies, and top
              campaigns. Configure recipients and timing per workspace.
            </P>

            <H3 id="page-lenses">Lenses</H3>
            <P>
              Saved filtered views across all your data — e.g. "All commissions
              over $500 from GoHighLevel in the last 30 days". Lenses can be
              shared with teammates or pinned to your Command Center.
            </P>

            <H3 id="page-people">People</H3>
            <P>
              Workspace-level contacts roster: teammates with access, recent
              activity, and per-person attribution.
            </P>

            {/* ============== Settings reference ============== */}
            <H2 id="settings">Settings reference</H2>

            <H3 id="set-profile">Profile</H3>
            <P>Name, avatar, timezone, language, and tour reset.</P>

            <H3 id="set-notifications">Notifications</H3>
            <P>
              Per-channel toggles (email, in-app, Slack) for anomalies,
              digests, team activity, and billing events.
            </P>

            <H3 id="set-account">Account</H3>
            <P>
              Email, password, 2FA, connected social logins, danger-zone
              account deletion.
            </P>

            <H3 id="set-workspaces">Workspaces</H3>
            <P>Create, rename, archive, delete, or transfer workspaces.</P>

            <H3 id="set-team">Team & roles</H3>
            <P>
              Invite teammates, change roles, revoke seats, and see pending
              invites. Role definitions live in{" "}
              <a href="#invite-team" className="text-primary hover:underline">
                Getting started
              </a>
              .
            </P>

            <H3 id="set-billing">Billing</H3>
            <P>
              Current plan, next renewal date, payment method, invoice history,
              and plan changes. Upgrades prorate immediately; downgrades apply
              at next renewal.
            </P>

            <H3 id="set-integrations">Integrations</H3>
            <P>
              Connect, reconnect, or disconnect platforms: GoHighLevel,
              ClickFunnels, Slack, generic webhooks. Per-workspace overrides
              live inside each workspace's settings.
            </P>

            <H3 id="set-exports">Exports</H3>
            <P>
              On-demand CSV exports of commissions, contacts, and imports.
              Exports are generated async and emailed when ready.
            </P>

            <H3 id="set-support">Support</H3>
            <P>
              File and track support tickets. Replies appear in-app and via
              email.
            </P>

            {/* ============== Billing ============== */}
            <H2 id="billing">Plans & billing</H2>
            <P>
              Every plan starts with a 14-day free trial. You can upgrade,
              downgrade, or cancel at any time. See current pricing on the{" "}
              <Link to="/pricing" className="text-primary hover:underline">
                Pricing page
              </Link>
              .
            </P>
            <UL>
              <li><strong>Starter</strong> — 1 workspace, 1 seat, core importing & anomalies.</li>
              <li><strong>Pro</strong> — Multiple workspaces, team seats, all integrations including GoHighLevel, full anomaly engine.</li>
              <li><strong>Super</strong> — Everything in Pro plus unlimited workspaces, higher import limits, priority support.</li>
            </UL>

            {/* ============== Security ============== */}
            <H2 id="security">Security & data</H2>
            <UL>
              <li>Postgres row-level security scoped to each account — no cross-account data leakage.</li>
              <li>OAuth tokens encrypted at rest with per-tenant keys.</li>
              <li>Every admin and impersonation action is logged immutably.</li>
              <li>You can export and delete all your data at any time from Settings → Exports / Account.</li>
              <li>Connection scopes shown explicitly on the integration screen before authorize.</li>
            </UL>

            {/* ============== Support ============== */}
            <H2 id="support">Getting help</H2>
            <P>Three ways to reach us, in order of speed:</P>
            <OL>
              <li>
                <strong>In-app support:</strong>{" "}
                <strong>Settings → Support → New ticket</strong>. Replies come
                back in-app and via email.
              </li>
              <li>
                <strong>Email:</strong>{" "}
                <a
                  className="text-primary hover:underline"
                  href="mailto:support@superaffiliateos.com"
                >
                  support@superaffiliateos.com
                </a>
              </li>
              <li>
                <strong>Contact form:</strong>{" "}
                <Link to="/contact" className="text-primary hover:underline">
                  /contact
                </Link>
                .
              </li>
            </OL>
            <P>We respond to every message within one business day.</P>

            <div className="mt-16 rounded-2xl border border-border bg-card p-8 text-center">
              <h2 className="text-xl font-semibold">Ready to set up?</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Start your 14-day free trial — no charge until day 15.
              </p>
              <div className="mt-6 flex justify-center gap-3">
                <Button asChild>
                  <Link to="/signup">Start free trial</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/contact">Talk to us</Link>
                </Button>
              </div>
            </div>
          </article>
        </div>
      </section>
    </MarketingShell>
  );
}
